/** The File Groups dialogs: name a group (new / rename), and pick documents to add. */
import { useEffect, useId, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputLabel,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	TextField,
	Typography,
} from '@mui/material';
import { ErrorNotice, StatusBadge } from '../../shared/ui';
import { KindLabel } from '../shared/badges';
import { count } from '../../utils/format';
import type { FileRecord } from '../../schemas';

/* A dialog field's label: above the field at the body size, not MUI's
   floating label, which shrinks to 0.75em on the border — too small to read. */
const LABEL_SX = {
	position: 'static',
	transform: 'none',
	mb: '6px',
	fontSize: 'var(--fs-body)',
	fontWeight: 600,
	color: 'var(--ink)',
} as const;

const documents = (n: number) => `${count(n)} document${n === 1 ? '' : 's'}`;

/** A group's name: creating one, or renaming it. */
export function NameDialog({
	open,
	title,
	initial,
	confirmLabel,
	pending,
	error,
	taken,
	onClose,
	onSubmit,
}: {
	open: boolean;
	title: string;
	initial: string;
	confirmLabel: string;
	pending: boolean;
	error: unknown;
	/** True when the (trimmed) name is already another group's. */
	taken?: (name: string) => boolean;
	onClose: () => void;
	onSubmit: (name: string) => void;
}) {
	const fieldId = useId();
	const [name, setName] = useState(initial);
	useEffect(() => {
		if (open) setName(initial);
	}, [open, initial]);
	const trimmed = name.trim();
	const duplicate = taken?.(trimmed) ?? false;
	const ready =
		trimmed !== '' && trimmed !== initial.trim() && !duplicate && !pending;

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<Box
				component="form"
				onSubmit={(event) => {
					event.preventDefault();
					if (ready) onSubmit(trimmed);
				}}
			>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<InputLabel htmlFor={fieldId} sx={LABEL_SX}>
						Group name
					</InputLabel>
					<TextField
						id={fieldId}
						autoFocus
						fullWidth
						size="small"
						placeholder="e.g. Sales 2025"
						value={name}
						onChange={(event) => setName(event.target.value)}
						error={duplicate}
						helperText={
							duplicate
								? 'A group with this name already exists for this client.'
								: undefined
						}
						slotProps={{ htmlInput: { maxLength: 120 } }}
					/>
					<ErrorNotice error={error} />
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						type="submit"
						variant="contained"
						loading={pending}
						disabled={!ready}
					>
						{confirmLabel}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
}

/** The client's files not yet in the group, to tick and add. */
export function AddFilesDialog({
	open,
	files,
	pending,
	error,
	onClose,
	onAdd,
}: {
	open: boolean;
	files: FileRecord[];
	pending: boolean;
	error: unknown;
	onClose: () => void;
	onAdd: (picked: string[]) => void;
}) {
	const [picked, setPicked] = useState<Set<string>>(new Set());
	const [search, setSearch] = useState('');
	useEffect(() => {
		if (open) {
			setPicked(new Set());
			setSearch('');
		}
	}, [open]);

	const shown = useMemo(() => {
		const needle = search.trim().toLowerCase();
		if (!needle) return files;
		return files.filter((file) =>
			`${file.originalName} ${file.periodLabel ?? ''}`
				.toLowerCase()
				.includes(needle),
		);
	}, [files, search]);

	const toggle = (id: string) =>
		setPicked((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Add documents</DialogTitle>
			<DialogContent dividers>
				<TextField
					type="search"
					size="small"
					fullWidth
					placeholder="Search name or period"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					sx={{ mb: 'var(--s2)' }}
				/>
				{shown.length === 0 ? (
					<Typography
						sx={{
							p: 'var(--s4)',
							textAlign: 'center',
							color: 'var(--ink-3)',
						}}
					>
						{files.length === 0
							? 'Every file of this client is already in the group.'
							: 'No files match.'}
					</Typography>
				) : (
					<List disablePadding dense>
						{shown.map((file) => (
							<ListItemButton
								key={file.pkFileId}
								onClick={() => toggle(file.pkFileId)}
							>
								<ListItemIcon sx={{ minWidth: 36 }}>
									<Checkbox
										edge="start"
										size="small"
										checked={picked.has(file.pkFileId)}
										tabIndex={-1}
										disableRipple
									/>
								</ListItemIcon>
								<ListItemText
									primary={file.originalName}
									secondary={<KindLabel file={file} />}
								/>
								<StatusBadge
									group="fileStatus"
									value={file.status}
								/>
							</ListItemButton>
						))}
					</List>
				)}
				<ErrorNotice error={error} />
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant="contained"
					loading={pending}
					disabled={picked.size === 0 || pending}
					onClick={() => onAdd([...picked])}
				>
					{picked.size > 0
						? `Add ${documents(picked.size)}`
						: 'Add documents'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
